import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  activateSubscription,
  getBillingCredits,
  getBillingPlans,
  getBillingSubscription,
  purchaseCredits,
  putBillingCycle,
  putBillingPlan,
  type BillingCycle,
  type BillingSubscription,
  type PlanId,
} from '../api/billing'

export const BILLING_SUBSCRIPTION_QUERY_KEY = 'billing-subscription'
export const BILLING_CREDITS_QUERY_KEY = 'billing-credits'
export const BILLING_PLANS_QUERY_KEY = 'billing-plans'

export function isSubscriptionPending(
  subscription: BillingSubscription | undefined | null
): boolean {
  if (!subscription?.enrolled) return false
  if (subscription.subscriptionActivation === 'active') return false
  if (subscription.subscriptionActivation === 'pending') return true
  // Legacy payload without the field: pending only if credits are null
  return subscription.credits == null
}

export function isSubscriptionActive(
  subscription: BillingSubscription | undefined | null
): boolean {
  return (
    subscription?.enrolled === true && !isSubscriptionPending(subscription)
  )
}

export function useBillingSubscription(enabled = true) {
  return useQuery({
    queryKey: [BILLING_SUBSCRIPTION_QUERY_KEY],
    queryFn: getBillingSubscription,
    enabled,
  })
}

export function useBillingCredits(
  enabled = true,
  options?: { refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: [BILLING_CREDITS_QUERY_KEY],
    queryFn: getBillingCredits,
    enabled,
    refetchInterval: options?.refetchInterval,
  })
}

export function useBillingPlans(
  cycle: BillingCycle | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: [BILLING_PLANS_QUERY_KEY, cycle ?? 'default'],
    queryFn: () => getBillingPlans(cycle),
    enabled,
  })
}

export function useBillingMutations() {
  const queryClient = useQueryClient()

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [BILLING_SUBSCRIPTION_QUERY_KEY],
      }),
      queryClient.invalidateQueries({ queryKey: [BILLING_CREDITS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [BILLING_PLANS_QUERY_KEY] }),
    ])
  }

  const activate = useMutation({
    mutationFn: (body?: {
      planId?: Exclude<PlanId, 'enterprise'>
      cycle?: BillingCycle
    }) => activateSubscription(body),
    onSuccess: async (data) => {
      // Force active UI immediately — do not wait on a lagging re-GET.
      const activated: BillingSubscription = {
        ...data,
        enrolled: data?.enrolled !== false,
        subscriptionActivation: 'active',
        paymentMethodReady: data?.paymentMethodReady ?? true,
      }
      queryClient.setQueryData([BILLING_SUBSCRIPTION_QUERY_KEY], activated)

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [BILLING_CREDITS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({ queryKey: [BILLING_PLANS_QUERY_KEY] }),
      ])

      try {
        const fresh = await getBillingSubscription()
        queryClient.setQueryData([BILLING_SUBSCRIPTION_QUERY_KEY], {
          ...fresh,
          // Activate just succeeded: never fall back to pending from a stale GET.
          enrolled: true,
          subscriptionActivation:
            fresh.subscriptionActivation === 'pending' && fresh.credits == null
              ? 'active'
              : (fresh.subscriptionActivation ?? 'active'),
          credits: fresh.credits ?? activated.credits,
        })
      } catch {
        // Keep the optimistic activated payload.
      }
    },
  })

  const changeCycle = useMutation({
    mutationFn: (cycle: BillingCycle) => putBillingCycle(cycle),
    onSuccess: invalidateAll,
  })

  const changePlan = useMutation({
    mutationFn: (planId: Exclude<PlanId, 'enterprise'>) =>
      putBillingPlan(planId),
    onSuccess: invalidateAll,
  })

  const buyCredits = useMutation({
    mutationFn: ({
      quantity,
      idempotencyKey,
    }: {
      quantity: number
      idempotencyKey: string
    }) => purchaseCredits(quantity, idempotencyKey),
    onSuccess: invalidateAll,
  })

  return { activate, changeCycle, changePlan, buyCredits, invalidateAll }
}
